// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The shell's own navigation, as data (S5-06).
//
// THE MEASUREMENT THIS EXISTS FOR
// -------------------------------
// The Search Console export of 2026-09-06:
//
//   grep -oE '<a [^>]*href="' dist/*.html dist/play/*.html   ->   0 matches, all twelve pages
//
// This site had **no internal link a crawler could follow** without executing JavaScript, and the
// crawl said so: 2 of 76 requests were discovery, 74 were refreshes of the three pages Google
// already had, and nine of the twelve sitemap URLs sat at "Discovered - currently not indexed".
//
// The links themselves were never missing. `SiteFooter` has drawn nine of them since S5-01 and the
// arcade tiles have named their machines since S5-04 — in the React tree, which is a thing that
// exists a few hundred milliseconds after the document a crawler reads.
//
// WHY THE LISTS ARE HERE AND NOT IMPORTED FROM THE CLIENT
// ------------------------------------------------------
// **Every module in `src/seo/` has no imports at all**, and that is load-bearing rather than tidy:
// `vite.config.ts` reaches `prerender.ts` and Vite's native config loader resolves a config's
// imports with Node's rules. `src/client/navigation.ts` imports `./pointer` with no extension,
// which is a warning on every build at best and a resolution failure at worst — so reaching into
// the client from here would put the whole build behind an import graph it cannot see.
//
// The copy is therefore deliberate, and it is held to the client by
// `test/seo/prerenderLinks.test.ts`, which imports both sides and fails by name when they drift.
// That is the same arrangement the pre-paint theme script in `index.html` already lives under: one
// duplicate, one guard, and the guard names the file to change.
//
// WHAT THIS MUST NEVER DO
// -----------------------
// **Offer a link the rendered page does not.** `prerender.ts` states the rule and this is the
// sharpest way to break it: a footer written onto a route whose shell draws none is a link written
// for robots. `drawsFooter` is the whole of that decision and it mirrors `MACHINE_ROUTES`.

/** One link in the shell's chrome: where it goes and the words the client renders on it. */
export interface ChromeLink {
  readonly to: string
  /** The English label, which is what a prerendered page is written in — every page is `lang="en"`. */
  readonly text: string
}

/**
 * The primary navigation, which every route draws outside focus mode.
 *
 * `NAV_TABS` in `src/client/navigation.ts`, in its order. **All five, including the two a narrow
 * phone drops**: `tabsFor` answers `NAV_TABS` whole for any width it has not measured, and a
 * document that has never been laid out is exactly that case.
 *
 * Two of these — `/leaderboards` and `/settings` — are `index: false` and disallowed in
 * `robots.txt`. They are written anyway because the rendered bar draws them, and a nav that
 * silently omits what the player can see is the same lie in the other direction. A crawler is
 * perfectly able to read a link it has been asked not to follow.
 */
export const PRIMARY_NAV: readonly ChromeLink[] = [
  { to: '/arcade', text: 'Arcade' },
  { to: '/leaderboards', text: 'Boards' },
  { to: '/', text: 'Play' },
  { to: '/builder', text: 'Build' },
  { to: '/settings', text: 'You' },
]

/** What the primary navigation is called to a screen reader — `nav.primary` in the bundle. */
export const PRIMARY_NAV_LABEL = 'Primary'

/**
 * The footer, on the routes that draw one. `SiteFooter`'s own list, in its order.
 */
export const FOOTER_NAV: readonly ChromeLink[] = [
  { to: '/arcade', text: 'Machines' },
  { to: '/how-to-play', text: 'How to play' },
  { to: '/pricing', text: 'Pricing' },
  { to: '/connect', text: 'Connect your AI' },
  { to: '/headset', text: 'Open on your headset' },
  { to: '/support', text: 'Support' },
  { to: '/privacy', text: 'Privacy' },
  { to: '/accessibility', text: 'Accessibility' },
  { to: '/terms', text: 'Terms' },
  { to: '/developers', text: 'Developers' },
  { to: '/about', text: 'About' },
]

/** What the footer nav is called to a screen reader — `footer.heading` in the bundle. */
export const FOOTER_NAV_LABEL = 'More from HCC Pinball'

/** The line `SiteFooter` closes with. */
export const FOOTER_NOTE = '© 2026 Holmes Computer Consultants'

/**
 * Every path the shell's own chrome links, from both navigations (S5-07).
 *
 * `sitemap.ts` reads this to decide *how* a refused route is refused. A route nothing links is
 * cheapest stopped at the crawl with `Disallow`; a route this list names has to stay crawlable and
 * say `noindex` instead, because `Disallow` stops the fetch and a page Google may not fetch is a
 * page whose `noindex` it can never read.
 */
export const CHROME_LINKED_PATHS: readonly string[] = [
  ...new Set([...PRIMARY_NAV, ...FOOTER_NAV].map((link) => link.to)),
]

/**
 * The one machine page that never touches the network (S7-08).
 *
 * `useMachine` answers `bundledMachine()` and **returns before any fetch** when the id is
 * `BUNDLED_MACHINE_ID`, and the bundle's flagship is `neon-nova` — so the two machine pages
 * behave differently and a rule written against `/play/*` would be wrong for one of them either
 * way it was written.
 *
 * A literal, because `chrome.ts` cannot import `machineSource.ts` without pulling the client into
 * the build's import graph. `test/seo/prerenderLinks.test.ts` holds it to `BUNDLED_MACHINE_ID`,
 * so bundling a different flagship fails by name rather than quietly withholding a hint from the
 * page that grew to need one.
 */
export const BUNDLED_MACHINE_ROUTE = '/play/neon-nova'

/**
 * Does this route make a network request as it loads?
 *
 * Only the routes that answer `true` are given a `preconnect` to the Supabase origin. The
 * checklist calls S7-08 "the easiest to get wrong" and this is why: a preconnect on a page that
 * fetches nothing opens a socket for nothing, and the front door makes **zero** requests by design
 * (FR-030) — `landing.spec.ts` would be right to fail it.
 *
 * Read out of the code rather than assumed:
 *
 *   * `Arcade.tsx` calls `useRoster(true)`, unconditionally.
 *   * `PlayRoute.tsx` calls `useMachine`, which fetches for every machine but the bundled one.
 *   * `Landing.tsx` and `Pricing.tsx` import nothing that reaches the network.
 *   * `/builder` is deliberately absent: its route is lazy and nothing in it was found to fetch
 *     on load. **A missed hint is a missed optimisation; a hint on a page that never connects is
 *     the defect this task names.** If the Builder gains a load-time fetch, add it here.
 */
export function fetchesOnLoad(route: string): boolean {
  if (route === '/arcade') return true
  if (route.startsWith('/play/')) return route !== BUNDLED_MACHINE_ROUTE
  return false
}

/**
 * Does this route's shell draw a footer?
 *
 * **`/` is a machine route.** `MACHINE_ROUTES` is `['/', '/play/:machineId']`, and `AppShell`
 * withholds `SiteFooter` on every one of them — M8-78, after a player on a phone reported "all
 * that stuff between the bottom of the pinball machine and the bottom navigation bar". So the
 * front door has no footer either, and writing one here would be a page that differs from the one
 * that hydrates.
 *
 * The cost that decision accepted is written down in `AppShell` and is unchanged by this file:
 * the documents are reached from `/arcade`, from `/pricing` and from each other. What this file
 * changes is only *when* — those links now exist before JavaScript rather than after it.
 */
/**
 * Does this page build a machine on load, and therefore need the renderer (S7-09)?
 *
 * `/` runs the flagship in attract mode and `/play/{machine}` runs the machine it names. Nothing
 * else does: the arcade's tiles draw their cabinets through `TileAttract`, which has always fetched
 * `attractCabinet` dynamically, and the Builder is behind `LAZY_ROUTES` already.
 *
 * **Written out rather than expressed as `!drawsFooter`.** The two happen to agree on every route
 * today, because a page that is all cabinet is also a page with no room for a footer — but they are
 * different questions, and a design that ever gives a machine page a header would move one and not
 * the other. `test/seo/prerenderLinks.test.ts` holds this to `isMachineRoute` in the client, which
 * is the list that actually decides.
 */
export function runsAMachine(route: string): boolean {
  return route === '/' || route.startsWith('/play/')
}

export function drawsFooter(route: string): boolean {
  return route !== '/' && !route.startsWith('/play/')
}
